import React, { useEffect, useState } from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { getPublicPrograms } from "../api/programsAPI";
import { useSelector } from "react-redux";
import { selectClubName } from "../store/slices/settingsSlice";
import gymnasticsSilhouette from "../assets/system-images/gymnastics/gymnastics-silhouette-1.jpg";
import { useTheme } from "../context/ThemeContext";
import OnboardingWalkthrough, { OnboardingStep } from "../components/common/OnboardingWalkthrough";
import { useOnboarding } from "../hooks/useOnboarding";
import SEOHead from "../components/seo/SEOHead";
import BreadcrumbSchema from "../components/seo/BreadcrumbSchema";

interface PublicProgram {
  id: string;
  key: string;
  label?: string;
  description?: string;
  image_url?: string | null;
  min_age?: number;
  max_age?: number;
  hours_per_week?: number;
  category_id?: string | null;
  featured?: boolean;
}

interface PublicCategory {
  id: string | null;
  name: string;
  description?: string | null;
  background_color?: string | null;
  background_image_url?: string | null;
  programs: PublicProgram[];
}

interface PublicProgramsFlat {
  layout: "flat";
  programs: PublicProgram[];
}
interface PublicProgramsGrouped {
  layout: "grouped";
  featured: PublicProgram[];
  categories: PublicCategory[];
}

type PublicProgramsResponse = PublicProgramsFlat | PublicProgramsGrouped;

const ProgramCard: React.FC<{ program: PublicProgram; isDarkMode: boolean; onboardingIndex?: number }> = ({
  program,
  isDarkMode,
  onboardingIndex,
}) => (
  <div
    key={program.id}
    data-onboarding={onboardingIndex === 0 ? "program-card" : undefined}
    className={`${isDarkMode ? "bg-gray-800" : "bg-white"} shadow-md rounded-xl overflow-hidden transition transform hover:-translate-y-1 hover:shadow-2xl duration-300 relative`}
  >
    <div
      className={`absolute bottom-0 right-0 w-[85%] h-[85%] bg-no-repeat bg-contain pointer-events-none ${isDarkMode ? "opacity-10" : "opacity-20"}`}
      style={{
        backgroundImage: `url(${program.image_url || gymnasticsSilhouette})`,
        backgroundPosition: "bottom right",
        filter: "brightness(1) contrast(1)",
      }}
    ></div>
    <div className="p-5 flex flex-col h-full relative z-10">
      <h3 className={`text-lg sm:text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-800"}`}>
        {program.label || "Untitled Program"}
      </h3>
      {program.description?.trim() && (
        <p className={`text-sm flex-1 mb-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
          {program.description}
        </p>
      )}
      <div className={`text-xs sm:text-sm space-y-1 mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        {program.min_age != null && program.max_age != null && (
          <p>
            <span className="font-medium">Age Range:</span> {program.min_age} - {program.max_age}
          </p>
        )}
        {program.hours_per_week != null && (
          <p>
            <span className="font-medium">Hours/Week:</span> {program.hours_per_week}
          </p>
        )}
      </div>
    </div>
  </div>
);

const ProgramsPage = () => {
  const [response, setResponse] = useState<PublicProgramsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const clubName = useSelector(selectClubName);
  const { isDarkMode } = useTheme();

  const onboardingSteps: OnboardingStep[] = [
    {
      target: '[data-onboarding="programs-section"]',
      title: "Welcome to Programs",
      content: "Browse our programs by category, with featured programs highlighted at the top.",
      placement: "bottom",
      highlightPadding: 12,
    },
    {
      target: '[data-onboarding="program-card"]',
      title: "Program Cards",
      content: "Each card shows a program's name, description, age range, and hours per week.",
      placement: "top",
      highlightPadding: 8,
    },
  ];

  const { shouldShow, isActive, markAsCompleted, markAsDismissed, startWalkthrough } = useOnboarding({
    // Distinct from the admin "Manage Programs" tour. Both pages used
    // onboarding-manage-programs-admin, so finishing either one silently
    // suppressed the other for any staff user who visits both.
    storageKey: "onboarding-programs-member",
    autoStart: false,
  });

  // Have any programs to show? Used to gate the onboarding tour.
  const totalProgramCount = (() => {
    if (!response) return 0;
    if (response.layout === "flat") return response.programs.length;
    return (
      response.featured.length +
      response.categories.reduce((sum, c) => sum + c.programs.length, 0)
    );
  })();

  useEffect(() => {
    if (!loading && shouldShow && !isActive && totalProgramCount > 0) {
      const timer = setTimeout(() => startWalkthrough(), 500);
      return () => clearTimeout(timer);
    }
  }, [loading, shouldShow, isActive, startWalkthrough, totalProgramCount]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicPrograms();
        if (cancelled) return;
        // Backend returns either { layout: 'flat', programs: [] } OR
        // { layout: 'grouped', featured: [], categories: [] }. Normalize.
        const data = res?.data?.data;
        if (data && typeof data === "object" && "layout" in data) {
          setResponse(data as PublicProgramsResponse);
        } else if (Array.isArray(data)) {
          // Legacy shape — backend before the marketing-surface
          // upgrade returned the bare array.
          setResponse({ layout: "flat", programs: data as PublicProgram[] });
        }
      } catch {
        // Handled by axios interceptor
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const primaryText = isDarkMode ? "text-white" : "text-gray-800";
  const sectionDesc = isDarkMode ? "text-gray-300" : "text-gray-600";

  return (
    <div className={`flex flex-col min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-white"}`}>
      <SEOHead
        title="Programs"
        description={`Explore programs offered by ${clubName || "our club"}. Find the right fit for every age and skill level.`}
      />
      <BreadcrumbSchema items={[{ name: "Programs", path: "/programs" }]} />
      <Navbar transparent={true} />

      <section data-onboarding="programs-section" className={`px-4 sm:px-6 py-12 sm:py-16 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        <div className="container mx-auto">
          {loading && <p className={`text-center ${sectionDesc}`}>Loading programs...</p>}

          {!loading && totalProgramCount === 0 && (
            <p className={`text-center ${sectionDesc}`}>No programs found.</p>
          )}

          {!loading && response?.layout === "flat" && response.programs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {response.programs.map((p, idx) => (
                <ProgramCard key={p.id} program={p} isDarkMode={isDarkMode} onboardingIndex={idx} />
              ))}
            </div>
          )}

          {!loading && response?.layout === "grouped" && (
            <>
              {response.featured.length > 0 && (
                <div className="mb-12">
                  <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${primaryText}`}>Featured</h2>
                  <div className={`h-1 w-16 rounded mb-6 ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {response.featured.map((p, idx) => (
                      <ProgramCard key={p.id} program={p} isDarkMode={isDarkMode} onboardingIndex={idx} />
                    ))}
                  </div>
                </div>
              )}

              {response.categories.map((cat) => (
                <div key={cat.id || cat.name} className="mb-12 last:mb-0">
                  <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${primaryText}`}>{cat.name}</h2>
                  {cat.description && (
                    <p className={`text-sm sm:text-base mb-4 ${sectionDesc}`}>{cat.description}</p>
                  )}
                  {/* Category dividers keep the per-category override (admins can pick a non-brand
                      accent color for categories). When the category leaves it null the divider is
                      neutral gray: the club's brand color is kept for actions and links. */}
                  <div
                    className={`h-1 w-16 rounded mb-6 ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
                    style={cat.background_color ? { backgroundColor: cat.background_color } : undefined}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {cat.programs.map((p) => (
                      <ProgramCard key={p.id} program={p} isDarkMode={isDarkMode} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      {shouldShow && (
        <OnboardingWalkthrough
          steps={onboardingSteps}
          storageKey="onboarding-programs-member"
          onComplete={markAsCompleted}
          onDismiss={markAsDismissed}
          isActive={isActive}
        />
      )}

      <Footer />
    </div>
  );
};

export default ProgramsPage;
